import { createContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";

import {
  deleteServiceService,
  getCategoryItemsService,
  getServicesService,
  createServiceService,
  getServiceService,
  editServiceService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { generateUniqueString } from "../../../utils";

export const serviceAdminContext = createContext({
  params: [],
  setParams: () => {},
  step: 0,
  setStep: () => {},
  paramsDeleted: [],
  setParamsDeleted: () => {},
  services: [],
  setServices: () => {},
  categories: [],
  setCategories: () => {},
  service: {},
  setService: () => {},
  search: "",
  setSearch: () => {},
  categoryId: "",
  setCategoryId: () => {},
  unique_id: "",
  setUnique_id: () => {},
  name: "",
  setName: () => {},
  description: "",
  setDescription: () => {},
  price: "",
  setPrice: () => {},
  service_category_name: "",
  setService_category_name: () => {},
  poster: "",
  setPoster: () => {},
  icon: "",
  setIcon: () => {},
  added_cities: [],
  setAdded_cities: () => {},
  has_discount: null,
  setHas_discount: () => {},
  is_active: null,
  setIs_active: () => {},
  always_discount: null,
  setAlways_discount: () => {},
  special: null,
  setSpecial: () => {},
  service_category_id: null,
  setService_category_id: () => {},
  isShowEditParamModal: null,
  setIsShowEditParamModal: () => {},
  handleShowEditParamModal: () => {},
  handleCloseEditParamModal: () => {},
  isEdit: null,
  setIsEdit: () => {},
  parameterType: null,
  setParameterType: () => {},
  handleGetService: () => {},
  handleShowModal: () => {},
  handleCloseModal: () => {},
  handleGetServices: () => {},
  handleDeleteService: () => {},
  handleGetCategories: () => {},
  handleCreateService: () => {},
  handleEditService: () => {},
  handleAddParam: () => {},
  handleEditParamsService: () => {},
  handleEditParamValue: () => {},
  handleDeleteParamValue: () => {},
  handleEditParamOrder: () => {},
});

const ServiceAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const cities = useSelector((state) => state.cities);

  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [service, setService] = useState({});
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState(null);
  const [isShowModal, setIsShowModal] = useState(false);
  // States for create and update
  const [added_cities, setAdded_cities] = useState([]);
  const [unique_id, setUnique_id] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [service_category_name, setService_category_name] = useState("");
  const [poster, setPoster] = useState("");
  const [icon, setIcon] = useState("");
  const [has_discount, setHas_discount] = useState(false);
  const [is_active, setIs_active] = useState(false);
  const [always_discount, setAlways_discount] = useState(false);
  const [special, setSpecial] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [service_category_id, setService_category_id] = useState(null);
  // States for Params
  const [params, setParams] = useState([]);
  const [step, setStep] = useState(0);
  const [paramsDeleted, setParamsDeleted] = useState([]);
  const [isShowEditParamModal, setIsShowEditParamModal] = useState(false);
  const [parameterType, setParameterType] = useState(null);

  const handleResetState = () => {
    setUnique_id("");
    setName("");
    setDescription("");
    setService_category_name("");
    setPrice("");
    setPoster("");
    setIcon("");
    setHas_discount(false);
    setIs_active(false);
    setAlways_discount(false);
    setSpecial(false);
    setIsEdit(false);
    setService_category_id(null);
    setService({});
    setParams([]);
    setParamsDeleted([]);
    setParameterType(null);
    setAdded_cities([]);
  };

  // Create Service Modal Operations
  const handleShowModal = () => setIsShowModal(true);
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  // Edit Param Service Modal Operations
  const handleShowEditParamModal = () => {
    setIsShowEditParamModal(true);
    setIsShowModal(false);
  };
  const handleCloseEditParamModal = () => {
    setIsShowEditParamModal(false);
    handleResetState();
    setStep(0);
  };

  // Handle Get Categories Method
  const handleGetCategories = async (event) => {
    event?.preventDefault();

    try {
      const { status, data } = await getCategoryItemsService();
      if (status === 200) {
        setCategories(data.list);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    }
  };

  // Handle Get Services Method
  const handleGetServices = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(categoryId && {
        service_category_id: categoryId,
      }),
      search,
      offset: 0,
      limit: 300,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getServicesService(objData);
      if (status === 200) {
        setServices(data.list);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Service Method
  const handleGetService = async (unique_id, func) => {
    const objData = {
      unique_id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getServiceService(objData);
      if (status === 200) {
        setIsEdit(true);
        setService(data);
        setUnique_id(data?.unique_id);
        setName(data?.name);
        setDescription(data?.description);
        setPrice(data?.price);
        setService_category_name(data?.service_category_name);
        setPoster(data?.poster);
        setIcon(data?.icon);
        setHas_discount(data?.has_discount);
        setIs_active(data?.is_active);
        setAlways_discount(data?.always_discount);
        setSpecial(data?.special);
        setService_category_id(data?.service_category_id);
        setParams(data?.params);
        setAdded_cities(
          data.cities?.map((_item) => ({ label: _item.name, value: _item.id }))
        );

        // Handle Custom Function
        if (func) {
          func();
        }
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Service Method
  const handleCreateService = async (event) => {
    event?.preventDefault();

    if (!unique_id) return toastErrorMessage("کد دوحرفی یکتا الزامی میباشد");
    if (!name) return toastErrorMessage("نام خدمت الزامی میباشد");

    const objData = {
      unique_id,
      name,
      description,
      service_category_id,
      has_discount,
      deleted: [],
      params: [],
      is_active,
      service_category_name,
      always_discount,
      special,
      price,
      poster,
      icon,
      added_cities: added_cities.map((_item) => _item.value),
      deleted_cities: [],
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createServiceService(objData);
      if (status === 200) {
        toastSuccessMessage("خدمت با موفقیت افزوده شد");
        handleCloseModal();
        handleGetServices();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Service Method
  const handleEditService = async (event) => {
    event?.preventDefault();

    if (!unique_id) return toastErrorMessage("کد دوحرفی یکتا الزامی میباشد");
    if (!name) return toastErrorMessage("نام خدمت الزامی میباشد");

    const deleted_cities = cities
      .filter((_c) => {
        const isExist = !isEmpty(
          added_cities.find((_nc) => _nc.value === _c.value)
        );
        return !isExist;
      })
      .map((_item) => _item.value);

    const objData = {
      ...service,
      deleted: [],
      name,
      description,
      service_category_id,
      has_discount,
      is_active,
      service_category_name,
      always_discount,
      special,
      price,
      poster,
      icon,
      added_cities: added_cities.map((_item) => _item.value),
      deleted_cities,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editServiceService(objData);
      if (status === 200) {
        toastSuccessMessage("خدمت با موفقیت ویرایش شد");
        handleCloseModal();
        handleGetServices();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Service Method
  const handleDeleteService = async (id) => {
    const objData = {
      unique_id: id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteServiceService(objData);
      if (status === 200) {
        toastSuccessMessage(`خدمت  ${id} با موفقیت حذف شد`);
        handleGetServices();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit ParamsService Method
  const handleEditParamsService = async () => {
    const objData = {
      ...service,
      deleted: [],
      params: [
        ...params.map((_p) => ({ ..._p, is_deleted: false })),
        ...paramsDeleted.map((_p) => ({ ..._p, is_deleted: true })),
      ],
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editServiceService(objData);
      if (status === 200) {
        toastSuccessMessage("خدمت با موفقیت ویرایش شد");
        handleCloseEditParamModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Add Param Method
  const handleAddParam = async (event, _data, func) => {
    event?.preventDefault();

    if (!parameterType && parameterType !== 0)
      return toastErrorMessage("نوع پارامتر الزامی میباشد");

    let parameter = {};

    // Check length
    let order = _data ? _data?.params?.length || 0 : params?.length || 0;

    const parameterName = generateUniqueString("param-");

    switch (parameterType) {
      case 0:
        parameter = {
          type: "number",
          order,
          min: 0,
          max: 0,
          default: 0,
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "_عدد_",
        };
        break;
      case 1:
        parameter = {
          type: "switch",
          order,
          default: false,
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "__سویچ__",
        };
        break;
      case 2:
        parameter = {
          type: "datetime",
          order,
          start_hour: 0,
          end_hour: 23,
          wait_time: 120,
          wait_type: 0,
          wait_type_option: { value: 0, label: "دقیقه" },
          interval: 1,
          alert: "",
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "__روز_ساعت__",
        };
        break;
      case 3:
        parameter = {
          type: "text",
          order,
          alert: "",
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "__متن__",
        };
        break;
      case 4:
        parameter = {
          type: "multiplechoice",
          order,
          alert: "",
          list: "",
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "_لیست_کشویی_",
        };
        break;
      case 5:
        parameter = {
          type: "image",
          order,
          name: "__عکس__",
          unique_id: parameterName,
          alert: "",
          description: "",
          description_extra: "",
          parameter: parameterName,
        };
        break;
      case 6:
        parameter = {
          type: "date",
          order,
          start_hour: 0,
          end_hour: 23,
          wait_time: 120,
          wait_type: 0,
          wait_type_option: { value: 0, label: "دقیقه" },
          interval: 1,
          alert: "",
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "__روز_ساعت__",
        };
        break;
      case 7:
        parameter = {
          type: "time",
          order,
          start_hour: 0,
          end_hour: 23,
          wait_time: 120,
          wait_type: 0,
          wait_type_option: { value: 0, label: "دقیقه" },
          interval: 1,
          alert: "",
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "__روز_ساعت__",
        };
        break;
      case 8:
        parameter = {
          type: "radiobutton",
          order,
          alert: "",
          list: "",
          description: "",
          description_extra: "",
          parameter: parameterName,
          name: "__لیست_تکی__",
        };
        break;
      case 9:
        parameter = {
          type: "item",
          order,
          name: "__آیتم__",
          unique_id: `${service.unique_id}${parameterName.slice(-2)}`,
          multi: false,
          alert: "",
          params: [],
        };
        break;

      default:
        break;
    }

    parameter = { ...parameter, isNew: true, is_deleted: false };

    // Add params
    if (_data) {
      handleEditParamValue(_data, "params", [..._data?.params, parameter]);
    } else {
      setParams([...params, parameter]);
    }

    if(func) func(order)
  };

  // Handle Edit Param Value
  const handleEditParamValue = (data, name, _value, unique_id) => {
    if (unique_id) {
      let items = [...params];
      let itemIndex = items?.findIndex((v) => v?.unique_id === unique_id);
      let item = items[itemIndex];

      // Change Item params
      let vals = [...item?.params];
      let valIndex = vals?.findIndex((v) =>
        data?.type === "item"
          ? v?.unique_id === data?.unique_id
          : v?.parameter === data?.parameter
      );
      let val = vals[valIndex];
      val[name] = _value;
      vals[valIndex] = val;

      // save in item params
      item["params"] = vals;
      items[itemIndex] = item;

      // save in master params
      setParams(items);
    } else {
      let vals = [...params];
      let valIndex = vals?.findIndex((v) =>
        data?.type === "item"
          ? v?.unique_id === data?.unique_id
          : v?.parameter === data?.parameter
      );
      let val = vals[valIndex];
      val[name] = _value;
      vals[valIndex] = val;
      setParams(vals);
    }
  };

  // Handle Delete Param Value
  const handleDeleteParamValue = (_data, u_id) => {
    if (u_id) {
      let items = [...params];
      let itemIndex = items?.findIndex((v) => v?.unique_id === u_id);
      let item = items[itemIndex];

      // Change Item params
      let vals = [...item?.params];
      let filtredValue = vals?.filter((v) =>
        _data?.type === "item"
          ? v?.unique_id !== _data?.unique_id
          : v?.parameter !== _data?.parameter
      );
      let findValue = vals?.find((v) =>
        _data?.type === "item"
          ? v?.unique_id === _data?.unique_id
          : v?.parameter === _data?.parameter
      );

      // save in item params
      item["params"] = filtredValue;
      items[itemIndex] = item;
      // Save delete param
      if (!_data?.isNew)
        setParamsDeleted([
          ...paramsDeleted,
          {
            ...findValue,
            ...(_data?.type !== "item" && { unique_id: u_id }),
          },
        ]);

      // save in master params
      setParams(items);
    } else {
      let vals = [...params];
      let filtredValue = vals?.filter((v) =>
        _data?.type === "item"
          ? v?.unique_id !== _data?.unique_id
          : v?.parameter !== _data?.parameter
      );
      let findValue = vals?.find((v) =>
        _data?.type === "item"
          ? v?.unique_id === _data?.unique_id
          : v?.parameter === _data?.parameter
      );
      // Save delete param
      if (!_data?.isNew)
        setParamsDeleted([
          ...paramsDeleted,
          {
            ...findValue,
            ...(_data?.type !== "item" && { unique_id: service?.unique_id }),
          },
        ]);

      // Change params
      setParams(filtredValue);
    }
  };

  // Handle Edit Param Order
  const handleEditParamOrder = (index, parent, directopn) => {
    if (parent) {
      // Find oarent
      let items = [...params];
      let itemIndex = items?.findIndex(
        (v) => v?.unique_id === parent?.unique_id
      );
      let item = items[itemIndex];

      // Change order
      let vals = [...item?.params];
      let temp = vals[index].order;
      vals[index].order = vals[index - 1].order;
      vals[index - 1].order = temp;

      // save in item params
      item["params"] = vals;
      items[itemIndex] = item;

      // save in master params
      setParams(items);
    } else {
      let vals = [...params];
      let temp = vals[index].order;
      vals[index].order = vals[index - 1].order;
      vals[index - 1].order = temp;

      setParams(vals);
      setStep(index + (directopn ? 0 : -1));
    }
  };

  return (
    <serviceAdminContext.Provider
      value={{
        services,
        setServices,
        categories,
        setCategories,
        service,
        setService,
        search,
        setSearch,
        categoryId,
        setCategoryId,
        isShowModal,
        unique_id,
        setUnique_id,
        name,
        setName,
        description,
        setDescription,
        price,
        setPrice,
        service_category_name,
        setService_category_name,
        poster,
        setPoster,
        icon,
        setIcon,
        setIsShowModal,
        has_discount,
        setHas_discount,
        is_active,
        setIs_active,
        always_discount,
        setAlways_discount,
        special,
        setSpecial,
        service_category_id,
        setService_category_id,
        added_cities,
        setAdded_cities,
        isEdit,
        setIsEdit,
        isShowEditParamModal,
        setIsShowEditParamModal,
        parameterType,
        setParameterType,
        params,
        setParams,
        step,
        setStep,
        paramsDeleted,
        setParamsDeleted,
        handleShowEditParamModal,
        handleCloseEditParamModal,
        handleGetService,
        handleShowModal,
        handleCloseModal,
        handleGetServices,
        handleDeleteService,
        handleGetCategories,
        handleCreateService,
        handleEditService,
        handleAddParam,
        handleEditParamsService,
        handleEditParamValue,
        handleDeleteParamValue,
        handleEditParamOrder,
      }}
    >
      {children}
    </serviceAdminContext.Provider>
  );
};

export default ServiceAdminContext;
